import { useParams, Link } from 'react-router-dom';
import { blogPosts } from '@/mocks/blogPosts';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';
import NotFound from '@/pages/NotFound';

const BlogDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return <NotFound />;
  }

  const firstParagraph = post.content.find(b => b.type === 'paragraph')?.text || post.excerpt;

  const articleJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": post.title,
      "description": post.excerpt,
      "image": post.image,
      "datePublished": "2026-06-15",
      "dateModified": "2026-06-29",
      "author": {
        "@type": "Person",
        "name": post.author.name,
        "jobTitle": post.author.role
      },
      "publisher": {
        "@type": "Organization",
        "name": "BitNinja Technologies",
        "url": "https://bitninjatech.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://storage.readdy-site.link/project_files/932d0adb-bf56-44ff-a11c-0aa982709dd7/0499b76b-9ae9-4384-9aac-227cd2d8a42c_compressed_BITNINJA-final-image.webp"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://bitninjatech.com/blog/${post.slug}`
      },
      "articleBody": firstParagraph,
      "url": `https://bitninjatech.com/blog/${post.slug}`
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://bitninjatech.com" },
        { "@type": "ListItem", "position": 2, "name": "Field Notes", "item": "https://bitninjatech.com/#blog" },
        { "@type": "ListItem", "position": 3, "name": post.title, "item": `https://bitninjatech.com/blog/${post.slug}` }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SeoHead
        title={`${post.title} | Field Notes | BitNinja Technologies`}
        description={post.excerpt.substring(0, 160)}
        canonicalUrl={`https://bitninjatech.com/blog/${post.slug}`}
        ogImage={post.image}
        ogType="article"
        jsonLd={articleJsonLd}
      />
      <Navbar />
      <article className="pt-28 md:pt-32 pb-16 md:pb-24">
        <div className="section-padding">
          <div className="max-w-3xl mx-auto">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors mb-8 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>

            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500/10 text-primary-500 text-[10px] font-mono uppercase tracking-widest">
                {post.category}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-foreground-500">
                {post.date} &middot; {post.readTime}
              </span>
            </div>

            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold leading-[1.02] tracking-tight text-foreground-900 mb-8">
              {post.title}
            </h1>

            <div className="flex items-center gap-4 mb-10 pb-10 border-b border-background-200">
              <img
                alt={post.author.name}
                className="w-12 h-12 rounded-full object-cover"
                src={post.author.image}
              />
              <div>
                <div className="text-sm font-semibold text-foreground-900">{post.author.name}</div>
                <div className="text-xs text-foreground-500">{post.author.role}</div>
              </div>
            </div>

            <div className="relative h-[350px] md:h-[450px] rounded-2xl overflow-hidden mb-10">
              <img
                alt={post.title}
                className="w-full h-full object-cover object-center"
                src={post.image}
              />
            </div>

            <div className="prose-custom">
              {post.content.map((block, index) => {
                if (block.type === 'heading') {
                  return (
                    <h2 key={index} className="font-display text-2xl font-bold text-foreground-900 mt-10 mb-4">
                      {block.text}
                    </h2>
                  );
                }
                return (
                  <p key={index} className="text-[15px] leading-relaxed text-foreground-700 mb-5">
                    {block.text}
                  </p>
                );
              })}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Home
              </Link>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-2 bg-foreground-900 hover:bg-foreground-800 text-background-50 px-6 py-3 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                Get in Touch
                <i className="ri-arrow-right-up-line text-base"></i>
              </Link>
            </div>
          </div>
        </div>
      </article>
      <Footer />
    </div>
  );
};

export default BlogDetailPage;